import moment from "moment";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { getJob } from "../../../docuclipper/api";
import { logErrorToGA } from "../../../docuclipper/Logger";
import { ReduxState } from "../../../redux";
import { setJobData } from "../../../redux/reducers/JobData";
import { openModal } from "../../../redux/reducers/ModalManager";
import { fetchUser } from "../../../redux/reducers/User";
import { createAlert } from "../../../redux/reducers/Alerts";
import { useHistory } from "react-router-dom";
import { navigateAfterJobSuccess } from "src/utils/utils";
// {
//   type: 'update-current-job',
//     payload: {
//       progress: 60,
//       job: {}
//     }
// }

type Props = {};

const INITIAL_POLLING_INTERVAL_SECS = 5;
const REGULAR_POLLING_INTERVAL_SECS = 30;
const INITIAL_POLLS_COUNT = 3;

export const JobPoller: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const interval = React.useRef<null | number | NodeJS.Timeout>(null);
  const pollCount = React.useRef<number>(0);

  const { lastUpdate } = useSelector((state: ReduxState) => state.JobData);
  const jobStatus = useSelector((state: ReduxState) =>
    state.JobData.job ? state.JobData.job.status : null
  );
  const jobId = useSelector((state: ReduxState) =>
    state.JobData.job ? state.JobData.job.id : null
  );

  const lastUpdateRef = React.useRef<null | Date>(null);

  React.useEffect(() => {
    lastUpdateRef.current = lastUpdate;
  }, [lastUpdate]);

  const cleanup = () => {
    if (interval.current !== null) {
      // console.log("[JobPoller] Cleaning up interval, resetting poll count");
      clearInterval(interval.current as any);
      interval.current = null;
    }
    pollCount.current = 0;
  };

  const history = useHistory();

  const getCurrentPollingInterval = () => {
    const interval =
      pollCount.current < INITIAL_POLLS_COUNT
        ? INITIAL_POLLING_INTERVAL_SECS * 1000
        : REGULAR_POLLING_INTERVAL_SECS * 1000;
    // console.log(
    //   `[JobPoller] Current polling interval: ${interval / 1000}s (poll count: ${
    //     pollCount.current
    //   })`
    // );
    return interval;
  };

  const setup = () => {
    if (!["InProgress", "Finishing"].includes(jobStatus || "")) {
      // console.log(
      //   `[JobPoller] Not setting up polling - job status: ${jobStatus}`
      // );
      return;
    }
    if (interval.current !== null) {
      // console.log("[JobPoller] Polling already set up, skipping");
      return;
    }

    // console.log("[JobPoller] Setting up polling");

    const pollJob = () => {
      const now = moment();
      if (
        lastUpdateRef.current !== null &&
        lastUpdateRef.current >
          now
            .subtract(
              pollCount.current < INITIAL_POLLS_COUNT
                ? INITIAL_POLLING_INTERVAL_SECS
                : REGULAR_POLLING_INTERVAL_SECS,
              "seconds"
            )
            .toDate()
      ) {
        // console.log("[JobPoller] Skipping poll - too soon since last update");
        return;
      }

      if (jobId !== null && jobId !== undefined) {
        pollCount.current += 1;
        // console.log(
        //   `[JobPoller] Polling job ${jobId} (poll #${pollCount.current})`
        // );

        // If we're transitioning from initial to regular interval, reset the interval
        if (pollCount.current === INITIAL_POLLS_COUNT) {
          // console.log(
          //   "[JobPoller] Transitioning from initial to regular polling interval"
          // );
          cleanup();
          setup();
          return;
        }

        getJob(jobId)
          .then((jobRes) => {
            // console.log(
            //   `[JobPoller] Job status: ${jobRes.status}, progress: ${jobRes.progress}`
            // );
            if (jobRes.status === "OutOfCredits") {
              dispatch(
                createAlert({
                  timeout: 0,
                  type: "error",
                  messageWithUpdate: `You've hit your monthly page limit. Choose how to continue above.`,
                  message: "",
                })
              );
            }
            if (
              ["Succeeded", "Failed", "Canceled", "OutOfCredits"].includes(
                jobRes.status
              )
            ) {
              // console.log("[JobPoller] Job completed, fetching user data");
              dispatch(fetchUser());
            }

            if (["Succeeded"].includes(jobRes.status)) {
              // console.log("[JobPoller] Job succeeded, navigating");
              navigateAfterJobSuccess(history, dispatch);
            }

            dispatch(
              setJobData({
                job: {
                  status: jobRes.status,
                  output: jobRes.output,
                  isGeneric: jobRes.isGeneric,
                  isBankMode: jobRes.isBankMode,
                  bankModeIfReconciled: jobRes.bankModeIfReconciled,
                  isPartial: jobRes.isPartial,
                  name: jobRes.name,
                  type: jobRes.type,
                },
                progress: jobRes.progress || null,
              })
            );
          })
          .catch((err) => {
            // console.error("[JobPoller] Error polling job:", err);
            logErrorToGA(err);
          });
      }
    };

    interval.current = setInterval(pollJob, getCurrentPollingInterval());
    // console.log("[JobPoller] Polling initialized");
  };

  React.useEffect(() => {
    if (
      jobId !== null &&
      !["InProgress", "Finishing"].includes(jobStatus || "")
    ) {
      // console.log(
      //   `[JobPoller] Job ${jobId} no longer in progress, cleaning up`
      // );
      cleanup();
    }
    if (
      jobId !== null &&
      ["InProgress", "Finishing"].includes(jobStatus || "") &&
      interval.current === null
    ) {
      // console.log(`[JobPoller] Job ${jobId} in progress, setting up polling`);
      setup();
    }
  }, [jobId, jobStatus]);

  React.useEffect(() => {
    // console.log("[JobPoller] Initial setup");
    setup();
    return () => {
      // console.log("[JobPoller] Component unmounting, cleaning up");
      cleanup();
    };
  }, []);

  return null;
};
export default JobPoller;
